import { useMemo } from 'react'
import { format } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { LeaveListItem, Role } from '@lms/shared-types'
import { PageWithStatTiles } from '../components/templates'
import { PageHead } from '../components/primitives/PageHead'
import { Stat } from '../components/primitives/Stat'
import { Card } from '../components/primitives/Card'
import { FadeIn } from '../components/primitives/FadeIn'
import {
  UpcomingPressureRow,
  blackoutToPressure,
  holidayToPressure,
  todayIso,
  type UpcomingPressureItem,
} from '../components/director/UpcomingPressureRow'
import { RiskBandPanel, type RiskBandItem } from '../components/director/RiskBandPanel'
import { SickClusteringPanel, type SickCluster } from '../components/director/SickClusteringPanel'
import { PendingBacklogPanel, type PendingBacklogItem, urgencyForAge } from '../components/director/PendingBacklogPanel'
import { listLeaves } from '../api/leaves'
import { listBlackouts } from '../api/blackouts'
import { listPublicHolidays } from '../api/public-holidays'
import { fetchRiskBand, fetchSickClustering, fetchPendingBacklog } from '../api/director'
import { useSession } from '../auth/auth-context'
import { useNavCounts } from '../lib/use-nav-counts'

const PREVIEW_LIMIT = 3
const RECENT_LIMIT = 5
const UPCOMING_LIMIT = 2

/**
 * Director home — designed for Rajesh's actual day, not a manager's.
 *
 * Sections (top→bottom):
 *   1. Action tiles — composite decisions + blackout escalations from
 *      `useNavCounts`, both clickable.
 *   2. Awaiting your decision — top N composite requests, oldest
 *      recommended first.
 *   3. Upcoming pressure points — next blackouts and public holidays
 *      with a countdown, so coverage pressure is visible without
 *      opening the HOD overview (ported from `prototype/director.jsx`).
 *   4. Recent decisions made by this Director — quick audit trail.
 *
 * Intentionally absent vs the Manager Dashboard: peak-window banner,
 * at-capacity teams, "Out this week" stat. Those are HR / manager
 * surfaces; mixing them here makes the Director scroll past other
 * people's work to find their own.
 */
export function DirectorDashboardPage(): JSX.Element {
  const { user } = useSession()
  const role: Role = (user?.role as Role) ?? 'employee'
  const counts = useNavCounts(role)

  // Top N composite requests awaiting decision (oldest recommended first).
  // Uses the same endpoint as DirectorLeavesPage so the queue stays in
  // lockstep; we just slice the top N for the preview.
  const pendingQuery = useQuery({
    queryKey: ['director-dashboard', 'awaiting-decisions'],
    queryFn: () =>
      listLeaves({
        scope: 'all',
        status: 'escalated',
        limit: 200,
      }),
    refetchOnWindowFocus: false,
  })

  // Recent decisions made BY THIS DIRECTOR. We fetch approved + rejected
  // rows org-wide and filter client-side to leaves where `decidedBy ===
  // current user`. Without a dedicated /my-decisions endpoint this is
  // the cheapest read; the volumes are small (a Director makes ~5-20
  // overflow decisions a month).
  const myApprovedQuery = useQuery({
    queryKey: ['director-dashboard', 'my-approved', user?.id],
    queryFn: () =>
      listLeaves({ scope: 'all', status: 'approved', limit: 100 }),
    refetchOnWindowFocus: false,
    enabled: Boolean(user?.id),
  })
  const myRejectedQuery = useQuery({
    queryKey: ['director-dashboard', 'my-rejected', user?.id],
    queryFn: () =>
      listLeaves({ scope: 'all', status: 'rejected', limit: 100 }),
    refetchOnWindowFocus: false,
    enabled: Boolean(user?.id),
  })

  // ── Team-insight panels ──────────────────────────────────────────────────
  // Risk-band, sick-clustering, and pending-backlog are fetched from the
  // existing /api/v1/insights/* endpoints. Director has HR+ visibility
  // so RLS surfaces the full company.

  const riskBandQuery = useQuery({
    queryKey: ['director-dashboard', 'risk-band'],
    queryFn: fetchRiskBand,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const sickClusteringQuery = useQuery({
    queryKey: ['director-dashboard', 'sick-clustering'],
    queryFn: () => fetchSickClustering({ windowDays: 30, threshold: 3 }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const pendingBacklogQuery = useQuery({
    queryKey: ['director-dashboard', 'pending-backlog'],
    queryFn: () => fetchPendingBacklog({ minBusinessDays: 5, thresholdUnactioned: 1 }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  // Map API shapes to panel-prop shapes. Risk-band items: the API gives
  // aggregate counts, not per-leave rows. We synthesise synthetic items
  // from the counts so the RiskBandPanel renders a compact overview bar.
  const riskBandItems = useMemo<RiskBandItem[]>(() => {
    const d = riskBandQuery.data
    if (!d) return []
    const items: RiskBandItem[] = []
    if (d.high > 0) {
      items.push({
        leaveId: 'summary-high',
        employee: `${d.high} employee${d.high === 1 ? '' : 's'}`,
        riskScore: 85,
        riskBand: 'HIGH',
        modifier: -15,
      })
    }
    if (d.medium > 0) {
      items.push({
        leaveId: 'summary-medium',
        employee: `${d.medium} employee${d.medium === 1 ? '' : 's'}`,
        riskScore: 55,
        riskBand: 'MEDIUM',
        modifier: -8,
      })
    }
    if (d.low > 0) {
      items.push({
        leaveId: 'summary-low',
        employee: `${d.low} employee${d.low === 1 ? '' : 's'}`,
        riskScore: 20,
        riskBand: 'LOW',
        modifier: 0,
      })
    }
    return items
  }, [riskBandQuery.data])

  const sickClusters = useMemo<SickCluster[]>(() => {
    return (sickClusteringQuery.data?.clusters ?? []).map((c) => ({
      team: c.teamName,
      // employee names de-identified server-side (Malaysia medical confidentiality)
      employees: [],
      window: typeof c.date === 'string' ? c.date : String(c.date),
      count: c.employeeCount,
    }))
  }, [sickClusteringQuery.data])

  const backlogItems = useMemo<PendingBacklogItem[]>(() => {
    return (pendingBacklogQuery.data?.managers ?? []).map((m) => ({
      leaveId: m.managerId,
      ageDays: m.oldestAgeDays,
      employee: m.managerName,
      type: 'pending queue',
      dates: `${m.pendingCount} request${m.pendingCount === 1 ? '' : 's'}`,
      urgency: urgencyForAge(m.oldestAgeDays),
    }))
  }, [pendingBacklogQuery.data])

  const topPending = useMemo<LeaveListItem[]>(() => {
    const items = pendingQuery.data ?? []
    // FIFO — oldest escalated first.
    return [...items]
      .sort((a, b) => {
        const ax = a.createdAt ?? ''
        const bx = b.createdAt ?? ''
        return ax < bx ? -1 : ax > bx ? 1 : 0
      })
      .slice(0, PREVIEW_LIMIT)
  }, [pendingQuery.data])

  // Upcoming pressure points — blackouts + public holidays Director needs
  // to glance at without opening the HOD overview page. Both endpoints are
  // open to authenticated users; we merge, filter to dates from today
  // forward, sort soonest first, and slice the top N.
  const blackoutsQuery = useQuery({
    queryKey: ['director-dashboard', 'blackouts'],
    queryFn: () => listBlackouts(),
    refetchOnWindowFocus: false,
  })
  const holidaysQuery = useQuery({
    queryKey: ['director-dashboard', 'public-holidays'],
    queryFn: () => listPublicHolidays(),
    refetchOnWindowFocus: false,
  })

  const upcomingPressure = useMemo<UpcomingPressureItem[]>(() => {
    const today = todayIso()
    const items: UpcomingPressureItem[] = []
    for (const b of blackoutsQuery.data ?? []) {
      if (b.endDate < today) continue
      items.push(blackoutToPressure(b))
    }
    for (const h of holidaysQuery.data ?? []) {
      if (h.date < today) continue
      items.push(holidayToPressure(h))
    }
    return items
      .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0))
      .slice(0, UPCOMING_LIMIT)
  }, [blackoutsQuery.data, holidaysQuery.data])

  const recentDecisions = useMemo<LeaveListItem[]>(() => {
    if (!user?.id) return []
    const merged = [
      ...(myApprovedQuery.data ?? []),
      ...(myRejectedQuery.data ?? []),
    ]
    return merged
      .filter((l) => l.decidedById === user.id)
      .sort((a, b) => {
        const ax = a.decidedAt ?? ''
        const bx = b.decidedAt ?? ''
        return ax > bx ? -1 : ax < bx ? 1 : 0
      })
      .slice(0, RECENT_LIMIT)
  }, [myApprovedQuery.data, myRejectedQuery.data, user?.id])

  // The "awaiting your decision" surface is the escalated queue (pendingQuery).
  // The standalone escalations badge retired into the unified Approvals count.
  const decisionsCount = pendingQuery.data?.length ?? 0
  const escalationsCount = counts['manager-requests'] ?? 0

  return (
    <PageWithStatTiles
      statCols={2}
      head={
        <PageHead
          eyebrow="Director"
          title="Decision queue"
          subtitle="Manager-recommended overflow requests and blackout escalations wait here for your sign-off."
        />
      }
      tiles={
        <>
          <Link
            to="/director/leaves"
            data-testid="director-tile-decisions"
            style={tileLinkStyle}
          >
            <Stat
              label="Awaiting your decision"
              value={
                pendingQuery.isLoading && decisionsCount === 0
                  ? '—'
                  : decisionsCount
              }
              hint="composite leave requests"
            />
          </Link>
          <Link
            to="/manager/requests"
            data-testid="director-tile-escalations"
            style={tileLinkStyle}
          >
            <Stat
              label="Blackout escalations"
              value={escalationsCount}
              hint="blackout-window overrides"
            />
          </Link>
        </>
      }
    >
      <FadeIn>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ── Awaiting your decision ─────────────────────────────────────── */}
          <Card>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Awaiting your decision</h2>
              {decisionsCount > PREVIEW_LIMIT ? (
                <Link to="/director/leaves" style={sectionLinkStyle}>
                  View all {decisionsCount} →
                </Link>
              ) : null}
            </div>
            {pendingQuery.isLoading ? (
              <div style={emptyStyle}>Loading…</div>
            ) : topPending.length === 0 ? (
              <div style={emptyStyle}>
                Inbox zero. No composite leave requests awaiting your decision
                right now.
              </div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {topPending.map((row) => (
                  <PendingDecisionRow key={row.id} row={row} />
                ))}
              </ul>
            )}
          </Card>

          {/* ── Upcoming pressure points ─────────────────────────────────────── */}
          <Card>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Upcoming pressure points</h2>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                next blackouts and public holidays
              </span>
            </div>
            {blackoutsQuery.isLoading || holidaysQuery.isLoading ? (
              <div style={emptyStyle}>Loading…</div>
            ) : upcomingPressure.length === 0 ? (
              <div style={emptyStyle}>
                Clear runway. Nothing flagged for the months ahead.
              </div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {upcomingPressure.map((item) => (
                  <UpcomingPressureRow key={item.key} item={item} />
                ))}
              </ul>
            )}
          </Card>

          {/* ── Team-insight panels (RFC-005 §5) ────────────────────────────── */}
          <RiskBandPanel
            items={riskBandItems}
            loading={riskBandQuery.isLoading}
          />
          <SickClusteringPanel
            clusters={sickClusters}
            loading={sickClusteringQuery.isLoading}
          />
          <PendingBacklogPanel
            items={backlogItems}
            loading={pendingBacklogQuery.isLoading}
          />

          {/* ── Recent decisions ─────────────────────────────────────────────── */}
          <Card>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Recent decisions</h2>
            </div>
            {myApprovedQuery.isLoading || myRejectedQuery.isLoading ? (
              <div style={emptyStyle}>Loading…</div>
            ) : recentDecisions.length === 0 ? (
              <div style={emptyStyle}>
                No recent decisions on record. Your past approvals and
                rejections will appear here.
              </div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {recentDecisions.map((row) => (
                  <RecentDecisionRow key={row.id} row={row} />
                ))}
              </ul>
            )}
          </Card>
        </div>
      </FadeIn>
    </PageWithStatTiles>
  )
}

// ── PendingDecisionRow ────────────────────────────────────────────────────────

interface PendingDecisionRowProps {
  row: LeaveListItem
}

function PendingDecisionRow({ row }: PendingDecisionRowProps): JSX.Element {
  const days = computeDays(row.startDate, row.endDate, row.halfDay)
  const escalatedAgo = relativeTimeAgo(row.createdAt)
  const notePreview =
    row.managerJustification && row.managerJustification.length > 140
      ? row.managerJustification.slice(0, 140) + '…'
      : row.managerJustification
  return (
    <li
      data-testid="director-pending-row"
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--french-gray-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14, color: 'var(--text)' }}>
            {row.employee.name}
          </strong>
          <span style={typeChipStyle}>{row.type}</span>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
            {row.startDate} → {row.endDate} · {days} day{days === 1 ? '' : 's'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {escalatedAgo ? (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              escalated {escalatedAgo}
            </span>
          ) : null}
          <Link
            to="/leave"
            search={{ id: row.id }}
            style={ctaLinkStyle}
            data-testid="director-pending-decide"
          >
            Decide →
          </Link>
        </div>
      </div>
      {notePreview ? (
        <blockquote
          style={{
            margin: '8px 0 0',
            padding: '6px 10px',
            borderLeft: '3px solid var(--steel-blue)',
            background: 'var(--surface-2)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}
        >
          {notePreview}
        </blockquote>
      ) : null}
    </li>
  )
}

// ── RecentDecisionRow ─────────────────────────────────────────────────────────

interface RecentDecisionRowProps {
  row: LeaveListItem
}

function RecentDecisionRow({ row }: RecentDecisionRowProps): JSX.Element {
  const isApproved = row.status === 'approved'
  const ago = relativeTimeAgo(row.decidedAt)
  return (
    <li
      data-testid="director-recent-row"
      style={{
        padding: '10px 0',
        borderBottom: '1px solid var(--french-gray-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: 999,
            background: isApproved ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: isApproved ? 'var(--success-fg)' : 'var(--danger-fg)',
          }}
        >
          {isApproved ? 'Approved' : 'Rejected'}
        </span>
        <strong style={{ fontSize: 13, color: 'var(--text)' }}>
          {row.employee.name}
        </strong>
        <span style={typeChipStyle}>{row.type}</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {row.startDate} → {row.endDate}
        </span>
        {ago ? (
          <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' }}>
            {ago}
          </span>
        ) : null}
        <Link
          to="/leave"
          search={{ id: row.id }}
          style={{ ...ctaLinkStyle, padding: '2px 8px', fontSize: 11 }}
        >
          View
        </Link>
      </div>
    </li>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

function computeDays(start: string, end: string, halfDay?: boolean | null): number {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const calendar = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1
  if (halfDay && start === end) return 0.5
  return calendar
}

function relativeTimeAgo(iso?: string | null): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (isNaN(t)) return null
  const deltaSec = Math.floor((Date.now() - t) / 1000)
  if (deltaSec < 60) return 'just now'
  const m = Math.floor(deltaSec / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  return format(new Date(iso), 'd MMM')
}

// ── styles ────────────────────────────────────────────────────────────────────

const tileLinkStyle: React.CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  borderRadius: 8,
  transition: 'transform 80ms ease-out',
}

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 8,
}

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--text-2)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

const sectionLinkStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--info-fg)',
  textDecoration: 'none',
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 0',
  textAlign: 'center',
  color: 'var(--text-3)',
  fontSize: 13,
}

const typeChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'capitalize',
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  color: 'var(--text-2)',
}

const ctaLinkStyle: React.CSSProperties = {
  padding: '4px 12px',
  borderRadius: 6,
  border: '1px solid var(--info-line)',
  background: 'var(--surface)',
  color: 'var(--info-fg)',
  fontSize: 12,
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
}
